import { fmtDay, fmtStamp, type Pallet, type Ref } from './model'
import { TONE, type Chip } from './orders'

// Dispatches and the load record (CONTROL V4 dRows / dFacts / dPallets /
// ddAddPanel; handoff §7n.6). The shapes are the API's (routes/dispatches.js)
// and so is every rule that decides a move: the record carries `moves`, and
// this file only turns what the API said into the words V4 shows.

export type LoadState = 'draft' | 'ready_to_load' | 'loading' | 'dispatched' | 'cancelled'

export interface Load {
  id: string
  reference: string
  state: LoadState
  state_label: string
  planned_date: string | null
  planned_time: string | null
  carrier: Ref | null
  location: Ref | null
  vehicle: string | null
  booking: string | null
  driver_name: string | null
  driver_contact: string | null
  instructions: string | null
  created: { by: string; at: string }
  departed: { at: string; by: string; how: string } | null
  pallets: number
  boxes: number
  orders: number
  verified: number
  unexpected: number
  discrepancy: boolean
}

export interface Verification {
  at: string
  by: string
  how: string
}

export interface GatedPallet extends Pallet {
  gates: string[]
}

export interface LoadPallet extends GatedPallet {
  verification: Verification | null
}

export interface LoadOrder {
  id: string
  reference: string
  approval_status: string
  customer_name: string | null
  destination_name: string | null
  final_destination?: string | null
  kind: string | null
  pallets: number
  boxes: number
}

export interface Unexpected {
  id: string
  pallet_id: string
  at: string
  by: string
  how: string
  other_load: string | null
}

export interface LoadDocument {
  order_id: string
  order_reference: string
  customer_name: string | null
  kinds: string[]
  final: boolean
  note: string
}

export interface LoadEvent {
  id: string
  type: string
  at: string
  what: string
  by: string
  how: string
  reason: string | null
}

export interface LoadMoves {
  add: boolean
  edit: boolean
  remove: boolean
  ready: { on: boolean; blocked: string }
  start: boolean
  reset: boolean
  scan: boolean
  verify_all: boolean
  depart: { on: boolean; blocked: string[] }
  cancel: boolean
  missing: string[]
}

export interface LoadRecord {
  load: Load
  orders: LoadOrder[]
  pallets: LoadPallet[]
  unexpected: Unexpected[]
  moves: LoadMoves
  documents: LoadDocument[]
  history: LoadEvent[]
}

export interface LoadOptions {
  carriers: Ref[]
  locations: Ref[]
}

// ---------------------------------------------------------------------------
// Chips
// ---------------------------------------------------------------------------

// V4 DSTATE: label, background, ink.
export const LSTATE: Record<LoadState, [string, string, string]> = {
  draft: ['Draft', '#f2ece1', '#5c5449'],
  ready_to_load: ['Ready to load', '#e8efd6', '#3f4a2c'],
  loading: ['Loading', '#fdf1e8', '#8c491a'],
  dispatched: ['Dispatched', '#dfe7d0', '#2f3a1f'],
  cancelled: ['Cancelled', '#f2ece1', '#8a8073'],
}

export function loadChip(state: LoadState): Chip {
  const s = LSTATE[state]
  return s ? { label: s[0], bg: s[1], ink: s[2] } : { label: state, bg: TONE.neutral[0], ink: TONE.neutral[1] }
}

const tone = (label: string, t: string): Chip => ({ label, bg: TONE[t][0], ink: TONE[t][1] })

// V4 dPallets ver: a departed load's pallets all read Departed.
export function verificationChip(load: Load, p: LoadPallet): Chip {
  if (load.state === 'dispatched') return tone('Departed', 'ok')
  return p.verification ? tone('Verified', 'ok') : tone('Not verified', 'neutral')
}

// The scan answer's colour (V4 dScanStyle): the API says good / warn / bad.
export const SCAN_TONE: Record<string, [string, string]> = {
  good: TONE.ok,
  warn: TONE.amber,
  bad: TONE.warn,
}

// ---------------------------------------------------------------------------
// The list
// ---------------------------------------------------------------------------

export type LoadFilter = '' | LoadState | 'disc'

export const LOAD_FILTERS: [LoadFilter, string][] = [
  ['', 'Any status'],
  ['draft', 'Draft'],
  ['ready_to_load', 'Ready to load'],
  ['loading', 'Loading'],
  ['dispatched', 'Dispatched'],
  ['cancelled', 'Cancelled'],
  ['disc', 'With discrepancies'],
]

// V4 dispatch sort: newest day first, earliest time first within a day.
// A load without a date sorts last.
export function sortLoads(ls: Load[]): Load[] {
  return ls.slice().sort((a, b) => {
    const da = a.planned_date || ''
    const db = b.planned_date || ''
    if (da !== db) return !da ? 1 : !db ? -1 : da < db ? 1 : -1
    const ta = a.planned_time || ''
    const tb = b.planned_time || ''
    if (ta !== tb) return ta < tb ? -1 : 1
    return a.reference.localeCompare(b.reference)
  })
}

// V4: search is over the load, carrier, booking and vehicle; 'disc' is a
// loading load with a pallet verified that is not on it.
export function filterLoads(ls: Load[], state: LoadFilter, q: string): Load[] {
  let out = sortLoads(ls)
  if (state) out = out.filter((l) => (state === 'disc' ? l.discrepancy : l.state === state))
  const s = q.trim().toLowerCase()
  if (s) out = out.filter((l) => [l.reference, l.carrier?.name, l.booking, l.vehicle].filter(Boolean).join(' ').toLowerCase().includes(s))
  return out
}

export const whenText = (l: Load) => (l.planned_date ? fmtDay(l.planned_date) + (l.planned_time ? ' · ' + l.planned_time : '') : 'Not scheduled')
export const vehicleText = (l: Load) => (l.vehicle || '—') + (l.booking ? ' · ' + l.booking : '')
export const ordersText = (n: number) => n + ' order' + (n === 1 ? '' : 's')
// A departed load was verified in full; before that, only live verifications count.
export const palletsText = (l: Load) => (l.state === 'dispatched' ? l.pallets : l.verified) + ' / ' + l.pallets + ' verified'

export function exportRows(ls: Load[]): (string | number)[][] {
  return [
    ['Load', 'Planned date', 'Planned time', 'Carrier', 'Vehicle', 'Booking', 'Collection location', 'Orders', 'Pallets', 'Verified', 'Boxes', 'Status', 'Departed at', 'Departed by'],
    ...ls.map((l) => [
      l.reference,
      l.planned_date || '',
      l.planned_time || '',
      l.carrier?.name || '',
      l.vehicle || '',
      l.booking || '',
      l.location?.name || '',
      l.orders,
      l.pallets,
      l.state === 'dispatched' ? l.pallets : l.verified,
      l.boxes,
      loadChip(l.state).label,
      l.departed ? fmtStamp(l.departed.at) : '',
      l.departed?.by || '',
    ]),
  ]
}

// ---------------------------------------------------------------------------
// Load details: New load and Edit details share one form
// ---------------------------------------------------------------------------

export interface DetailsForm {
  planned_date: string
  planned_time: string
  carrier_id: string
  location_id: string
  vehicle: string
  booking: string
  driver_name: string
  driver_contact: string
  instructions: string
}

// V4 dNew: the location last used on a load if it is still live, else a
// dock, else the first live one.
export function defaultLocation(loads: Load[], locations: Ref[]): string {
  const live = new Set(locations.map((l) => l.id))
  const used = loads
    .filter((l) => l.location && live.has(l.location.id))
    .sort((a, b) => (a.created.at < b.created.at ? -1 : a.created.at > b.created.at ? 1 : 0))
  const last = used[used.length - 1]?.location?.id
  return last || locations.find((l) => /dock/i.test(l.name))?.id || locations[0]?.id || ''
}

// V4 dNew: today, 07:30, the first live carrier.
export function newForm(loads: Load[], options: LoadOptions, today: string): DetailsForm {
  return {
    planned_date: today,
    planned_time: '07:30',
    carrier_id: options.carriers[0]?.id || '',
    location_id: defaultLocation(loads, options.locations),
    vehicle: '',
    booking: '',
    driver_name: '',
    driver_contact: '',
    instructions: '',
  }
}

export function formFrom(l: Load): DetailsForm {
  return {
    planned_date: l.planned_date || '',
    planned_time: l.planned_time || '',
    carrier_id: l.carrier?.id || '',
    location_id: l.location?.id || '',
    vehicle: l.vehicle || '',
    booking: l.booking || '',
    driver_name: l.driver_name || '',
    driver_contact: l.driver_contact || '',
    instructions: l.instructions || '',
  }
}

// Date, time and carrier are required (the API says which is missing).
export const formValid = (f: DetailsForm) => !!f.planned_date && /^\d{2}:\d{2}$/.test(f.planned_time) && !!f.carrier_id

// A choice already on the load stays offered after it is archived, so
// editing other details never forces a change of carrier or location.
export function withCurrentRef(live: Ref[], current: Ref | null): Ref[] {
  if (!current || live.some((r) => r.id === current.id)) return live
  return [...live, { id: current.id, name: current.name + ' (archived)' }]
}

// ---------------------------------------------------------------------------
// The record
// ---------------------------------------------------------------------------

// V4 dFacts.
export function loadFacts(l: Load): { k: string; v: string }[] {
  return [
    { k: 'Planned departure', v: whenText(l) },
    { k: 'Actual departure', v: l.departed ? fmtStamp(l.departed.at) + ' · ' + l.departed.by + ' · ' + l.departed.how : 'Not departed' },
    { k: 'Carrier', v: l.carrier?.name || 'Not recorded' },
    { k: 'Vehicle', v: l.vehicle || 'Not recorded' },
    { k: 'Booking reference', v: l.booking || 'Not recorded' },
    { k: 'Collection location', v: l.location?.name || 'Not recorded' },
    { k: 'Driver', v: [l.driver_name, l.driver_contact].filter(Boolean).join(' · ') || 'Not recorded' },
    { k: 'Instructions', v: l.instructions || 'None' },
  ]
}

export const FREIGHT_NOTE =
  'Freight references (forwarder, airway bill, flight, container) are stored on each export order, not on this truck. See the order’s Dispatches & documents tab.'

export function departedNote(l: Load): string {
  if (!l.departed) return ''
  return (
    'Departed ' +
    fmtStamp(l.departed.at) +
    ' · ' +
    l.departed.by +
    ' · ' +
    l.departed.how +
    '. Stock on hand reduced and order dispatched quantities incremented once; repeating this action does nothing.'
  )
}

// V4 dProgress: planned pallets verified, as a line and a percentage.
export function progress(rec: LoadRecord): { text: string; pct: number } {
  const total = rec.pallets.length
  const done = rec.pallets.filter((p) => p.verification).length
  return { text: done + ' of ' + total + ' pallets verified', pct: total ? Math.round((100 * done) / total) : 0 }
}

// V4 ddAddPanel row: what the pallet is and where it is going.
export function candidateText(p: GatedPallet): string {
  const src = p.source.variety + ' · ' + p.source.box_type
  const order = p.order ? p.order.reference + (p.order.customer_name ? ' → ' + p.order.customer_name : '') : 'No order'
  return p.boxes + ' boxes · ' + src + ' · ' + order
}

// ---------------------------------------------------------------------------
// Dispatch from the order (Adrian and Vanessa, 2026-11-02)
// ---------------------------------------------------------------------------

// Why one of the order's pallets cannot go on the truck now, or '' if it can.
// Only what the order record shows: the API checks every gate again (lids,
// source holds, approval) and names any pallet it refuses.
export function dispatchBlock(p: Pallet): string {
  if (p.departed) return 'Already departed'
  if (p.state === 'planned') return 'Not yet confirmed'
  if (p.state === 'discrepancy') return 'Count discrepancy unresolved'
  if (p.hold) return 'On hold'
  if (p.dispatch && p.dispatch.state !== 'cancelled') return 'On load ' + p.dispatch.reference
  return ''
}

// The open loads the order can join instead: live, with the carrier chosen,
// departing today, earliest first.
export function joinableLoads(ls: Load[], carrierId: string, today: string): Load[] {
  return sortLoads(
    ls.filter((l) => (l.state === 'draft' || l.state === 'ready_to_load' || l.state === 'loading') && l.carrier?.id === carrierId && l.planned_date === today)
  )
}

// The departure stamped on a new load: now, on this computer's clock.
export const nowTime = (now: Date = new Date()) => String(now.getHours()).padStart(2, '0') + ':' + String(now.getMinutes()).padStart(2, '0')
